import { type IClientProviderPluginInstaller, type ClientProviderPluginInstallOption } from '../models/plugin-type'
import { type IClientProviderPlugin, importModuleByUrl, assertSameOriginOrMatch } from '@source-digital/sd-app-core'
import { BaseClientProviderPluginInstaller } from '../base'
import { type IBrokerContext } from '../../context'
import { nanoid } from 'nanoid'
import { createScriptProviderPlugin } from '@source-digital/sd-app-client-adapter-script'
import { type ClientPluginFactory } from '@source-digital/sd-app-client-core'

function isClientPluginFactory (value: any): value is ClientPluginFactory<any> {
  return typeof value === 'function'
}

export interface ScriptPluginInstallOption extends ClientProviderPluginInstallOption<'script'> {
  src: string
  exportName?: string
  config?: any
}

export class ScriptClientProviderPluginInstaller extends BaseClientProviderPluginInstaller implements IClientProviderPluginInstaller<'script', ScriptPluginInstallOption> {
  constructor (ctx: IBrokerContext) {
    super(ctx, 'ScriptClientProviderPluginInstaller')
  }

  async install (installOption: ScriptPluginInstallOption): Promise<{ id: string, name?: string, plugin: IClientProviderPlugin }> {
    const { name } = installOption
    const module = await this.#importModule(installOption)
    const id = `script-${nanoid()}`
    await module.install(this.createContext(module, id))
    this.logger.log('Installed client plugin.', installOption)
    return {
      id,
      name,
      plugin: module
    }
  }

  async #importModule (installOption: ScriptPluginInstallOption): Promise<IClientProviderPlugin> {
    assertSameOriginOrMatch(installOption.src, this.ctx.initConfig.client?.scriptPluginAllowedOrigins)
    // const imported: Record<string, unknown> = await import(installOption.src)
    const exportNames: string[] = installOption.exportName != null ? [installOption.exportName] : ['default']

    const imported = await importModuleByUrl(installOption.src, exportNames)

    if (imported == null) {
      throw new Error(`Failed to import module: ${installOption.src}. It does not have any of the following exports: ${exportNames.join(', ')}`)
    }

    if (!isClientPluginFactory(imported)) {
      throw new Error(`Failed to import module: ${installOption.src}. It does not export a function.`)
    }

    return createScriptProviderPlugin(imported(installOption.config))
  }

  async uninstall (module: IClientProviderPlugin): Promise<void> {
    await module.uninstall()
  }
}
